import React, { memo, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import DropdownMenu from '@/components/General/DropdownMenu';
import FilterCondition from '@/components/Filters/FilterCondition';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import SearchBox from '@/components/General/inputs/SearchBox';
import GuideTarget from '@/components/Guides/GuideTarget';
import GuideSurface, { useGuideSurface } from '@/components/Guides/GuideSurface';
import usePlatform from '@/utils/hooks/usePlatform';
import useGuideBlocker from '@/utils/hooks/useGuideBlocker';

/**
 * Advanced Filter Builder Component
 * A modern, reusable component for building complex filter conditions
 * 
 * Landscape/Web: Shows a persistent sidebar with fields grouped by section.
 * Mobile: Shows a toggle button to expand a collapsible sidebar overlay with sectioned fields.
 * 
 * @param {object} props
 * @param {Array} props.conditions - Array of filter conditions
 * @param {string} props.logic - Filter logic ('AND' or 'OR')
 * @param {Array} props.unusedFields - Fields available to add
 * @param {object} props.groupedFields - Fields grouped by category
 * @param {Array} props.sectionedFields - Fields grouped by section with ordering
 * @param {Function} props.onAddCondition - Callback to add a condition
 * @param {Function} props.onUpdateCondition - Callback to update a condition
 * @param {Function} props.onRemoveCondition - Callback to remove a condition
 * @param {Function} props.onUpdateLogic - Callback to update filter logic
 * @param {Function} props.onClearFilters - Callback to clear all filters
 * @param {object} props.dynamicOptionsData - Data for dynamic options (e.g., { roles: [...] })
 * @param {boolean} props.showHeader - Whether to show the header section
 * @param {React.ReactNode} props.headerRight - Custom content for header right side
 */
const AdvancedFilterBuilder = ({
    conditions = [],
    logic = 'AND',
    // eslint-disable-next-line no-unused-vars
    unusedFields = [],
    // eslint-disable-next-line no-unused-vars
    groupedFields = {},
    sectionedFields = [],
    onAddCondition,
    onUpdateCondition,
    onRemoveCondition,
    onUpdateLogic,
    onClearFilters,
    dynamicOptionsData = {},
    showHeader = true,
    headerRight = null,
    guidePrefix = null,
}) => {
    const { isWeb } = usePlatform();
    const isLandscape = isWeb;
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [fieldSearch, setFieldSearch] = useState('');
    const fieldsScroll = useRef(null);
    const fieldsSurfaceID = guidePrefix ? `${guidePrefix}.fields` : null;
    const fieldsSurface = useGuideSurface(fieldsSurfaceID);
    // The mobile sidebar is a plain RN Modal, not a SheetModal — register it
    // as a guide blocker so an in-sheet tour suspends while it's open.
    useGuideBlocker(isSidebarOpen && !fieldsSurface.hosting);

    // Build sectioned sidebar data (all fields, with used field tracking)
    const usedFieldIds = useMemo(() => conditions.map(c => c.fieldId), [conditions]);
    const sidebarSections = useMemo(() => {
        const term = fieldSearch.trim().toLowerCase();
        return sectionedFields.map(section => ({
            ...section,
            fields: section.fields
                .map(f => ({
                    ...f,
                    isUsed: usedFieldIds.includes(f.id),
                }))
                .filter(f => !term || f.name.toLowerCase().includes(term)),
        })).filter(section => section.fields.length > 0);
    }, [sectionedFields, usedFieldIds, fieldSearch]);

    const hasConditions = conditions.length > 0;
    // First field the tour spotlights for "add a condition" — tapping it runs
    // the same onAddCondition the row itself uses.
    const firstUnusedField = useMemo(() => {
        for (const section of sidebarSections) {
            const field = section.fields.find(f => !f.isUsed);
            if (field) return field;
        }
        return null;
    }, [sidebarSections]);
    const renderFieldItem = field => {
        const item = (
            <Pressable
                style={[styles.sidebarItem, field.isUsed && styles.sidebarItemUsed]}
                onPress={() => {
                    if (field.isUsed) {
                        const conditionToRemove = conditions.find(c => c.fieldId === field.id);
                        if (conditionToRemove) onRemoveCondition(conditionToRemove.id);
                    } else {
                        onAddCondition(field);
                    }
                }}
            >
                {field.isUsed ? (
                    <Unicon name="checkmark-circle" size={16} colorVariant="primary" />
                ) : (
                    <Unicon name="add-circle-outline" size={16} colorVariant="primary" />
                )}
                <Text style={[styles.sidebarItemText, field.isUsed && styles.sidebarItemTextUsed]} numberOfLines={1}>{field.name}</Text>
            </Pressable>
        );
        return field.id === firstUnusedField?.id ? (
            <GuideTarget id={guidePrefix ? `${guidePrefix}.field` : null} activate={() => onAddCondition(field)}>{item}</GuideTarget>
        ) : item;
    };

    return (
        <View style={[styles.container, isLandscape && styles.containerLandscape]}>
            {/* Sidebar for landscape/web (always visible) */}
            {isLandscape && (
                <View style={styles.sidebar}>
                    <Text style={styles.sidebarTitle}>Add Filters</Text>
                    <View style={styles.sidebarSearch}>
                        <SearchBox
                            testID="FilterFieldSearch"
                            searchText={fieldSearch}
                            onChangeText={setFieldSearch}
                            textChangeDelay={0}
                            placeholder="Search fields..."
                            isNotOnSurface={false}
                        />
                    </View>
                    <ScrollView 
                        style={styles.sidebarScroll} 
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                    >
                        {sidebarSections.length === 0 ? (
                            <Text style={styles.sidebarEmptyText}>{fieldSearch ? 'No matching fields' : 'No fields available'}</Text>
                        ) : (
                            sidebarSections.map((section) => (
                                <View key={section.sectionName} style={styles.sidebarSection}>
                                    <Text style={styles.sidebarSectionTitle}>{section.sectionName}</Text>
                                    {section.fields.map(field => (
                                        <React.Fragment key={field.id}>{renderFieldItem(field)}</React.Fragment>
                                    ))}
                                </View>
                            ))
                        )}
                    </ScrollView>
                </View>
            )}

            {/* Mobile Sidebar Overlay (collapsible) */}
            {!isLandscape && (
                <GuideSurface
                    surfaceID={fieldsSurfaceID}
                    visible={isSidebarOpen}
                    animationType="slide"
                    transparent={true}
                    onRequestClose={() => setIsSidebarOpen(false)}
                >
                    <Pressable 
                        style={styles.mobileSidebarOverlay}
                        onPress={() => setIsSidebarOpen(false)}
                    >
                        <Pressable style={styles.mobileSidebarContainer} onPress={(e) => e.stopPropagation()}>
                            <View style={styles.mobileSidebarHeader}>
                                <Text style={styles.mobileSidebarTitle}>Add Filters</Text>
                                <GuideTarget id={guidePrefix ? `${guidePrefix}.fields.close` : null} activate={() => setIsSidebarOpen(false)}>
                                <Pressable onPress={() => setIsSidebarOpen(false)}>
                                    <Unicon name="close" size={24} colorVariant="text" />
                                </Pressable>
                                </GuideTarget>
                            </View>
                            <View style={styles.mobileSidebarSearch}>
                                <SearchBox
                                    testID="FilterFieldSearchMobile"
                                    searchText={fieldSearch}
                                    onChangeText={setFieldSearch}
                                    textChangeDelay={0}
                                    placeholder="Search fields..."
                                />
                            </View>
                            <ScrollView 
                                style={styles.mobileSidebarScroll}
                                ref={fieldsScroll}
                                showsVerticalScrollIndicator={false}

keyboardShouldPersistTaps="handled"
>
                                {sidebarSections.length === 0 ? (
                                    <Text style={styles.sidebarEmptyText}>{fieldSearch ? 'No matching fields' : 'No fields available'}</Text>
                                ) : (
                                    sidebarSections.map((section) => (
                                        <View key={section.sectionName} style={styles.sidebarSection}>
                                            <Text style={styles.sidebarSectionTitle}>{section.sectionName}</Text>
                                            {section.fields.map(field => (
                                                <GuideTarget key={field.id} id={guidePrefix && field.id === firstUnusedField?.id ? `${guidePrefix}.pick` : null} scrollRef={fieldsScroll} activate={() => {
                                                    onAddCondition(field);
                                                    fieldsSurface.selected();
                                                    setIsSidebarOpen(false);
                                                }}>
                                                <Pressable
                                                    key={field.id}
                                                    style={[styles.sidebarItem, field.isUsed && styles.sidebarItemUsed]}
                                                    onPress={() => {
                                                        if (field.isUsed) {
                                                            // Find the first condition with this fieldId and remove it
                                                            const conditionToRemove = conditions.find(c => c.fieldId === field.id);
                                                            if (conditionToRemove) {
                                                                onRemoveCondition(conditionToRemove.id);
                                                            }
                                                        } else {
                                                            onAddCondition(field);
                                                            if (fieldsSurface.hosting) {
                                                                fieldsSurface.selected();
                                                                setIsSidebarOpen(false);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {field.isUsed ? (
                                                        <Unicon name="checkmark-circle" size={16} colorVariant="primary" />
                                                    ) : (
                                                        <Unicon name="add-circle-outline" size={16} colorVariant="primary" />
                                                    )}
                                                    <Text style={[styles.sidebarItemText, field.isUsed && styles.sidebarItemTextUsed]} numberOfLines={1}>{field.name}</Text>
                                                </Pressable>
                                                </GuideTarget>
                                            ))}
                                        </View>
                                    ))
                                )}
                            </ScrollView>
                        </Pressable>
                    </Pressable>
                </GuideSurface>
            )}

            {/* Main content area */}
            <View style={[styles.mainContent, isLandscape && styles.mainContentLandscape]}>
                {/* Header Section */}
                {showHeader && (
                    <View style={styles.header}>
                        <View style={styles.headerLeft}>
                            <View style={styles.titleRow}>
                                <Unicon name="options" size={20} colorVariant="primary" />
                                <Text style={styles.title}>Filter Conditions</Text>
                                {hasConditions && (
                                    <View style={styles.badge}>
                                        <Text style={styles.badgeText}>{conditions.length}</Text>
                                    </View>
                                )}
                            </View>
                            {hasConditions && (
                                <Text style={styles.subtitle}>
                                    Showing results that match {logic === 'AND' ? 'all' : 'any'} of the following
                                </Text>
                            )}
                        </View>
                        <View style={styles.headerRight}>
                            {/* Sidebar Toggle Button - Mobile only */}
                            {!isLandscape && (
                                <GuideTarget id={guidePrefix ? `${guidePrefix}.field` : null} activate={() => setIsSidebarOpen(true)}>
                                <Pressable
                                    style={styles.sidebarToggleButton}
                                    onPress={() => setIsSidebarOpen(true)}
                                >
                                    <Unicon name="add" size={20} colorVariant="primary" />
                                    <Text style={styles.sidebarToggleText}>
                                        Add Filters
                                    </Text>
                                </Pressable>
                                </GuideTarget>
                            )}
                            {headerRight}
                        </View>
                    </View>
                )}

                {/* Action Bar */}
                <View style={styles.actionBar}>
                    <View style={styles.actionBarLeft}>
                        {/* Logic Toggle */}
                        {hasConditions && (
                            <DropdownMenu
                                guideTargetID={guidePrefix ? `${guidePrefix}.logic` : null}
                                title="Filter logic"
                                triggerComponent={
                                    <SolidButton 
                                        size="sm" 
                                        type="secondary" 
                                        text={logic === 'AND' ? 'Match All' : 'Match Any'}
                                        iconName={logic === 'AND' ? 'git-merge' : 'git-branch'}
                                        iconBefore
                                    />
                                }
                                options={[
                                    { 
                                        name: 'Match All (AND)', 
                                        subtitle: 'Results must match every condition',
                                        selected: logic === 'AND',
                                        onSelect: () => onUpdateLogic('AND'),
                                    },
                                    {
                                        name: 'Match Any (OR)',
                                        subtitle: 'Results can match any condition',
                                        selected: logic === 'OR',
                                        onSelect: () => onUpdateLogic('OR'),
                                    },
                                ]}
                            />
                        )}
                    </View>

                    <View style={styles.actionBarRight}>
                        {/* Clear Filters */}
                        {hasConditions && (
                            <GuideTarget id={guidePrefix ? `${guidePrefix}.clear` : null} activate={onClearFilters}>
                            <SolidButton
                                size="sm"
                                type="error"
                                text="Clear All"
                                iconName="trash-outline"
                                iconBefore
                                onPress={onClearFilters}
                            />
                            </GuideTarget>
                        )}
                    </View>
                </View>

                {/* Conditions List */}
                <View style={styles.conditionsList}>
                    {!hasConditions ? (
                    <View style={styles.emptyState}>
                        <EmptyListPlaceholder 
                            icon="funnel-outline"
                            message="No filters applied"
                            subMessage={isLandscape ? "Select a field from the sidebar to add a filter" : "Tap 'Add Filters' to select a field"}
                            spacing={20}
                        />
                    </View>
                    ) : (
                        conditions.map((condition, index) => {
                            const row = (
                                <FilterCondition
                                    key={condition.id}
                                    condition={condition}
                                    isFirst={index === 0}
                                    logic={logic}
                                    dynamicOptionsData={dynamicOptionsData}
                                    onUpdate={(updates) => onUpdateCondition(condition.id, updates)}
                                    onRemove={() => onRemoveCondition(condition.id)}
                                />
                            );
                            return index === 0
                                ? <GuideTarget key={condition.id} id={guidePrefix ? `${guidePrefix}.condition` : null}>{row}</GuideTarget>
                                : row;
                        })
                    )}
                </View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        flex: 1,
    },
    containerLandscape: {
        flexDirection: 'row',
        gap: 16,
    },
    sidebar: {
        width: 220,
        borderRightWidth: 1,
        borderRightColor: colors.border,
        paddingRight: 16,
    },
    sidebarTitle: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
        marginBottom: 12,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    sidebarSearch: {
        marginBottom: 12,
    },
    sidebarScroll: {
        flex: 1,
    },
    sidebarEmptyText: {
        fontSize: 13,
        color: colors.textSecondary,
        fontStyle: 'italic',
        paddingVertical: 8,
    },
    sidebarSection: {
        marginBottom: 16,
    },
    sidebarSectionTitle: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
        paddingBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    sidebarItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 6,
        paddingHorizontal: 8,
        borderRadius: 6,
        _web: {
            cursor: 'pointer',
            transition: 'all 0.15s',
            _hover: {
                backgroundColor: colors.hover,
            },
        },
    },
    sidebarItemUsed: {
        opacity: 0.6,
    },
    sidebarItemText: {
        fontSize: 13,
        color: colors.text,
        fontFamily: fonts.body,
        flex: 1,
    },
    sidebarItemTextUsed: {
        color: colors.textSecondary,
    },
    mobileSidebarOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        justifyContent: 'flex-end',
    },
    mobileSidebarContainer: {
        backgroundColor: colors.background,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        height: '90%',
    },
    mobileSidebarHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    mobileSidebarTitle: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    mobileSidebarSearch: {
        paddingHorizontal: 16,
        paddingTop: 12,
    },
    mobileSidebarScroll: {
        padding: 16,
        maxHeight: '100%',
    },
    sidebarToggleButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
    },
    sidebarToggleText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    mainContent: {
        flex: 1,
    },
    mainContentLandscape: {
        flex: 1,
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        marginBottom: 16,
    },
    headerLeft: {
        flex: 1,
    },
    headerRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    titleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    title: {
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    subtitle: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 4,
    },
    badge: {
        backgroundColor: colors.primary,
        borderRadius: 12,
        paddingHorizontal: 8,
        paddingVertical: 2,
        minWidth: 24,
        alignItems: 'center',
    },
    badgeText: {
        color: colors.onPrimary,
        fontSize: 12,
        fontFamily: fonts.bold,
    },
    actionBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 8,
    },
    actionBarLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    },
    actionBarRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    conditionsList: {
        flex: 1,
    },
    emptyState: {
        paddingVertical: 40,
        alignItems: 'center',
    },
}));

export default memo(AdvancedFilterBuilder);
